import type { DeviceSession } from '@/domain/entities/DeviceSession';
import type { LeadCaptureInput } from '@/domain/entities/LeadCaptureInput';

/**
 * Lead pendente de sincronização, persistido na object store `leadQueue` do
 * IndexedDB (HUB-236, Fatia 2). Convenção de nomes em camelCase — é
 * `HttpLeadRepository` (fronteira HTTP) quem traduz para `snake_case`, nunca
 * o domínio (Spec Técnica > Modelo de dados).
 *
 * `id` é um UUID gerado no dispositivo, usado só como chave local — nunca é
 * enviado ao servidor (`CaptureLeadRequest` não tem campo `id`; quem gera o
 * id definitivo do lead é sempre o backend, em `Lead.capture()`).
 *
 * `createdAt` é o timestamp do dispositivo, usado 100% localmente para
 * ordenar retries (mais antigo primeiro) — o backend não aceita nem usa
 * este campo (`created_at` do lead é sempre `datetime.now(UTC)` no
 * servidor, ver Spec Técnica > Correção 1 e ADR-003, Adendo).
 */
export interface QueuedLead {
  readonly id: string;
  readonly eventId: string;
  readonly deviceId: string;
  readonly name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly cpf: string | null;
  readonly customFields: Record<string, unknown>;
  readonly consentAccepted: boolean;
  readonly consentTextVersion: number;
  readonly createdAt: string;
  readonly retryCount: number;
  readonly lastRetryAt: string | null;
  readonly lastError: { status: number; message: string } | null;
}

/** Limite a partir do qual um alerta não-bloqueante é exibido ao operador
 * (CA-08) — o alerta dispara quando o total da fila **atinge** este valor. */
export const QUEUE_ALERT_THRESHOLD = 9_500;

/** Limite rígido: nenhum lead novo é aceito a partir daqui (CA-09) — a
 * criação é bloqueada quando a fila **já contém** este total. */
export const QUEUE_HARD_LIMIT = 10_000;

/**
 * Cria um novo `QueuedLead` a partir da sessão de dispositivo ativa e do
 * input já validado/tornado seguro para a rede (`toWireSafeLeadCaptureInput`,
 * HUB-201/HUB-237 CA-20) — CA-01. `phone`/`cpf`/`customFields` só são
 * preenchidos a partir da Fatia 3 (HUB-237), quando `CapturePluginLeadUseCase`
 * traduz o `LeadData` de um plugin real — o formulário fake (HUB-147)
 * continua sem coletá-los, então `input.phone`/`input.cpf`/
 * `input.customFields` seguem `undefined` nesse caminho e caem no default.
 */
export function createQueuedLead(
  session: DeviceSession,
  input: LeadCaptureInput,
  consentTextVersion: number
): QueuedLead {
  return {
    id: crypto.randomUUID(),
    eventId: session.eventId,
    deviceId: session.deviceId,
    name: input.name,
    email: input.email.trim().length > 0 ? input.email : null,
    phone: input.phone ?? null,
    cpf: input.cpf ?? null,
    customFields: input.customFields ?? {},
    consentAccepted: input.consentAccepted,
    consentTextVersion,
    createdAt: new Date().toISOString(),
    retryCount: 0,
    lastRetryAt: null,
    lastError: null,
  };
}

/**
 * Guarda de runtime usada antes de sincronizar (CA-02): um `QueuedLead`
 * gravado corretamente sempre tem `consentAccepted`/`consentTextVersion`
 * preenchidos — o TypeScript garante isso na escrita, mas não protege
 * contra um registro corrompido ou de uma versão de schema anterior já
 * residente no IndexedDB do dispositivo.
 */
export function hasCompleteConsentData(lead: QueuedLead): boolean {
  return (
    typeof lead.consentAccepted === 'boolean' &&
    typeof lead.consentTextVersion === 'number' &&
    Number.isFinite(lead.consentTextVersion) &&
    lead.consentTextVersion >= 1
  );
}

/** Status HTTP observados como falha **permanente** de sincronização —
 * nunca se resolvem sozinhos com retry, porque dependem de uma decisão do
 * lado do servidor que não muda ao reenviar o mesmo payload: gate de
 * consentimento não aprovado (409, `ConsentNotApprovedError`) e versão de
 * consentimento não encontrada (422, `ConsentVersionNotFoundError`). Todo o
 * resto (4xx transitório, 5xx, timeout local, offline) é retryable — pode se
 * resolver numa tentativa futura sem intervenção humana (LOCAL-002). */
const PERMANENT_SYNC_FAILURE_STATUSES: ReadonlySet<number> = new Set([409, 422]);

/**
 * Classifica o `status` de `QueuedLead.lastError` como falha permanente
 * (`true`) ou retryable (`false`). Usada por `SyncLeadQueueUseCase.syncOne`
 * (para logar de forma distinta) e por `useLeadQueueSync` (para derivar o
 * estado `'stuck'` do indicador visual, `OnlineStatusIndicator`) — a mesma
 * regra de classificação em um único lugar, sem duplicar a lista de status
 * entre as duas camadas.
 */
export function isPermanentSyncFailure(status: number): boolean {
  return PERMANENT_SYNC_FAILURE_STATUSES.has(status);
}

/** Converte o `QueuedLead` de volta para o formato aceito por
 * `HttpLeadRepository.captureLead` (`LeadCaptureInput`) — usado por
 * `SyncLeadQueueUseCase` para reaproveitar o repositório HTTP existente
 * (Fatia 1). Generalizado na Fatia 3 (HUB-237, CA-20) para incluir
 * `phone`/`cpf`/`customFields`, hoje só preenchidos por leads capturados via
 * plugin real — leads da captura fake seguem com esses 3 campos nulos/
 * vazios, sem mudança de comportamento (CA-02). */
export function toLeadCaptureInput(lead: QueuedLead): LeadCaptureInput {
  return {
    name: lead.name,
    email: lead.email ?? '',
    phone: lead.phone,
    cpf: lead.cpf,
    customFields: lead.customFields,
    consentAccepted: lead.consentAccepted,
  };
}
